// src/admin/pages/AdminDashboard.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FileText,
  Calendar,
  CheckSquare,
  GraduationCap,
  PenTool,
  Plus,
  Eye,
} from "lucide-react";
import { useAdminData } from "../context/AdminDataContext";
import Modal from "../components/Modal";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const {
    dailyCA,
    monthlyCA,
    prelimsTests,
    studentResults,
  } = useAdminData();

  const [selectedResult, setSelectedResult] = useState(null);

  // Compute 4 compact statistics
  const totalDailyPublished = (dailyCA || []).filter((i) => i?.status === "Published").length;
  const totalMonthlyPublished = (monthlyCA || []).filter((i) => i?.status === "Published").length;
  const totalPrelims = (prelimsTests || []).length;
  const totalSubmissions = (studentResults || []).length;

  const statCards = [
    {
      title: "Daily Current Affairs Published",
      count: totalDailyPublished,
      subtitle: `${dailyCA?.length || 0} total entries`,
      icon: FileText,
      link: "/admin/daily-ca",
    },
    {
      title: "Monthly Current Affairs Published",
      count: totalMonthlyPublished,
      subtitle: `${monthlyCA?.length || 0} monthly editions`,
      icon: Calendar,
      link: "/admin/monthly-ca",
    },
    {
      title: "Total Prelims Tests",
      count: totalPrelims,
      subtitle: "Free practice mock series",
      icon: CheckSquare,
      link: "/admin/prelims",
    },
    {
      title: "Total Student Submissions",
      count: totalSubmissions,
      subtitle: "Prelims & Mains answers",
      icon: GraduationCap,
      link: "/admin/results",
    },
  ];

  const quickActions = [
    {
      label: "Upload Daily Current Affairs",
      icon: FileText,
      to: "/admin/daily-ca?action=new",
    },
    {
      label: "Upload Monthly Current Affairs",
      icon: Calendar,
      to: "/admin/monthly-ca?action=new",
    },
    {
      label: "Create Prelims Test",
      icon: CheckSquare,
      to: "/admin/prelims?action=new",
    },
    {
      label: "Create Mains Question",
      icon: PenTool,
      to: "/admin/mains?action=new",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Heading */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 pb-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">
            Welcome Admin
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Overview and quick management of free UPSC initiatives.
          </p>
        </div>
      </div>

      {/* 4 Compact Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {statCards.map((card, idx) => {
          const Icon = card.icon;
          const cardBody = (
            <>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600 truncate">
                  {card.title}
                </span>
                <Icon size={16} className="text-slate-400 flex-shrink-0" />
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-2xl font-bold text-slate-900">
                  {card.count}
                </span>
                <span className="text-[11px] text-slate-400 truncate">
                  {card.subtitle}
                </span>
              </div>
            </>
          );

          return card.link ? (
            <Link
              key={idx}
              to={card.link}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:border-slate-300 transition-colors"
            >
              {cardBody}
            </Link>
          ) : (
            <div
              key={idx}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              {cardBody}
            </div>
          );
        })}
      </div>

      {/* Quick Action Buttons */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          {quickActions.map((qa, i) => {
            const Icon = qa.icon;
            return (
              <button
                key={i}
                onClick={() => navigate(qa.to)}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-200 hover:border-red-900 hover:bg-red-50/50 text-slate-700 hover:text-red-900 text-xs font-semibold transition-colors text-left"
              >
                <span className="flex items-center gap-2 truncate">
                  <Icon size={15} className="text-red-900 flex-shrink-0" />
                  <span className="truncate">{qa.label}</span>
                </span>
                <Plus size={14} className="text-slate-400 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Table 1: Recently Uploaded Current Affairs */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">
            Recently Uploaded Current Affairs
          </h3>
          <Link
            to="/admin/daily-ca"
            className="text-xs font-semibold text-red-900 hover:underline"
          >
            View all ({dailyCA?.length || 0})
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase text-[11px]">
                <th className="py-2.5 px-4">Title</th>
                <th className="py-2.5 px-3">Date</th>
                <th className="py-2.5 px-3">Category</th>
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(dailyCA || []).slice(0, 4).map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/60">
                  <td className="py-2.5 px-4 font-medium text-slate-800 max-w-sm truncate">
                    {item.title}
                  </td>
                  <td className="py-2.5 px-3 text-slate-500 whitespace-nowrap font-mono text-[11px]">
                    {item.date}
                  </td>
                  <td className="py-2.5 px-3 text-slate-500 whitespace-nowrap">
                    {item.category}
                  </td>
                  <td className="py-2.5 px-3 text-center whitespace-nowrap">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${
                        item.status === "Published"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap">
                    <button
                      onClick={() => navigate("/admin/daily-ca")}
                      className="text-xs text-red-900 font-semibold hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Table 2: Recently Created Prelims Tests */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">
            Recently Created Prelims Tests
          </h3>
          <Link
            to="/admin/prelims"
            className="text-xs font-semibold text-red-900 hover:underline"
          >
            View all ({prelimsTests?.length || 0})
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase text-[11px]">
                <th className="py-2.5 px-4">Test Name</th>
                <th className="py-2.5 px-3 text-center">Questions</th>
                <th className="py-2.5 px-3 text-center">Duration</th>
                <th className="py-2.5 px-3 text-center">Participants</th>
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(prelimsTests || []).slice(0, 4).map((test) => (
                <tr key={test.id} className="hover:bg-slate-50/60">
                  <td className="py-2.5 px-4 font-medium text-slate-800 max-w-sm truncate">
                    {test.title}
                  </td>
                  <td className="py-2.5 px-3 text-center font-semibold text-slate-700">
                    {test.questionsCount || (test.questions ? test.questions.length : 5)}
                  </td>
                  <td className="py-2.5 px-3 text-center text-slate-500 font-mono text-[11px]">
                    {test.duration || 15} mins
                  </td>
                  <td className="py-2.5 px-3 text-center font-bold text-emerald-800">
                    {test.participantsCount || 120}
                  </td>
                  <td className="py-2.5 px-3 text-center whitespace-nowrap">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${
                        test.status === "Published"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {test.status}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap">
                    <button
                      onClick={() => navigate("/admin/prelims")}
                      className="text-xs text-red-900 font-semibold hover:underline"
                    >
                      Edit Test
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Table 3: Recent Student Submissions */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">
            Recent Student Submissions
          </h3>
          <span className="text-xs font-semibold text-slate-500">
            Total: {studentResults?.length || 0}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase text-[11px]">
                <th className="py-2.5 px-4">Student Name</th>
                <th className="py-2.5 px-3">Test / Question</th>
                <th className="py-2.5 px-3 text-center">Initiative</th>
                <th className="py-2.5 px-3 text-center">Marks</th>
                <th className="py-2.5 px-3 font-mono">Date</th>
                <th className="py-2.5 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(studentResults || []).slice(0, 4).map((res) => (
                <tr key={res.id} className="hover:bg-slate-50/60">
                  <td className="py-2.5 px-4 font-semibold text-slate-800 whitespace-nowrap">
                    {res.studentName}
                  </td>
                  <td className="py-2.5 px-3 text-slate-600 max-w-xs truncate">
                    {res.initiativeName}
                  </td>
                  <td className="py-2.5 px-3 text-center whitespace-nowrap">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700">
                      {res.examType}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-center font-bold text-slate-900 whitespace-nowrap">
                    {res.marksObtained != null ? (
                      `${res.marksObtained} / ${res.totalMarks || 15}`
                    ) : (
                      <span className="text-amber-600 font-normal italic">Pending</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-slate-500 whitespace-nowrap font-mono text-[11px]">
                    {res.submissionDate}
                  </td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap">
                    <button
                      onClick={() => setSelectedResult(res)}
                      className="inline-flex items-center gap-1 text-xs text-red-900 font-semibold hover:underline"
                    >
                      <Eye size={13} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Result Modal */}
      {selectedResult && (
        <Modal
          isOpen={!!selectedResult}
          onClose={() => setSelectedResult(null)}
          title={`Submission: ${selectedResult.studentName}`}
          subtitle={`${selectedResult.initiativeName} (${selectedResult.examType})`}
          maxWidth="max-w-xl"
          footer={
            <div className="w-full flex justify-end">
              <button
                onClick={() => setSelectedResult(null)}
                className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold rounded-lg"
              >
                Close
              </button>
            </div>
          }
        >
          <div className="space-y-4 text-xs">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-800">{selectedResult.studentName}</p>
                <p className="text-slate-500">{selectedResult.studentEmail || "student@selvasias.in"}</p>
              </div>
              <div className="text-right">
                <span className="text-base font-bold text-red-900">
                  {selectedResult.marksObtained != null ? `${selectedResult.marksObtained} / ${selectedResult.totalMarks}` : "Pending"}
                </span>
                <p className="text-[11px] text-slate-500">{selectedResult.percentage ? `${selectedResult.percentage}%` : ""}</p>
              </div>
            </div>

            {selectedResult.examType === "Mains" && (
              <div className="space-y-2">
                <p className="font-semibold text-slate-700">Answer Script:</p>
                <div className="p-3 bg-white border border-slate-200 rounded-lg max-h-40 overflow-y-auto leading-relaxed text-slate-700">
                  {selectedResult.submittedAnswer || "Answer submitted by student."}
                </div>
                {selectedResult.feedback && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900">
                    <p className="font-semibold">Feedback:</p>
                    <p className="mt-1">{selectedResult.feedback}</p>
                  </div>
                )}
              </div>
            )}

            {selectedResult.examType === "Prelims" && (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1">
                <p className="font-semibold text-slate-700">Exam Performance:</p>
                <p className="text-slate-600">Correct: {selectedResult.correctCount ?? 4} | Incorrect: {selectedResult.incorrectCount ?? 1} | Unanswered: {selectedResult.unansweredCount ?? 0}</p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminDashboard;
